import { BASE_PATH_RUNTIME } from "@configs/runtimeConfig";
import queryKeys from "@configs/react-query/query-key";
import ToastConstant from "@configs/react-toastify/snackbar";
import { TOAST } from "@constants/index";
import paths from "@router/paths";
import i18n from "i18next";

/** Backend readable-view endpoints (application/json: { metadata, components }) */
export const SBOM_READABLE_VIEW_ENDPOINT = Object.freeze({
  VERIFICATION: queryKeys.verificationReportSbomReadableView,
  MERGED: queryKeys.mergedSbomReadableView,
  PRODUCT: queryKeys.productReportSbomReadableView,
  GROUP_PRODUCT: queryKeys.groupProductReportSbomReadableView,
});

export const SBOM_READABLE_VIEW_SOURCE = Object.freeze({
  VERIFICATION: "VERIFICATION",
  MERGED: "MERGED",
  PRODUCT: "PRODUCT",
  GROUP_PRODUCT: "GROUP_PRODUCT",
});

const SOURCE_TO_ENDPOINT = Object.freeze({
  [SBOM_READABLE_VIEW_SOURCE.VERIFICATION]: SBOM_READABLE_VIEW_ENDPOINT.VERIFICATION,
  [SBOM_READABLE_VIEW_SOURCE.MERGED]: SBOM_READABLE_VIEW_ENDPOINT.MERGED,
  [SBOM_READABLE_VIEW_SOURCE.PRODUCT]: SBOM_READABLE_VIEW_ENDPOINT.PRODUCT,
  [SBOM_READABLE_VIEW_SOURCE.GROUP_PRODUCT]: SBOM_READABLE_VIEW_ENDPOINT.GROUP_PRODUCT,
});

const basename = (BASE_PATH_RUNTIME || "/").replace(/\/+$/, "");

function throwPopupBlocked() {
  const error = new Error("POPUP_BLOCKED");
  error.code = "POPUP_BLOCKED";
  throw error;
}

/**
 * Build API request from readable-view page query params.
 * @param {Record<string, string>} query
 */
export function resolveSbomReadableViewRequest(query = {}) {
  const sourceType = String(query.sourceType || "").toUpperCase();
  const endpoint = SOURCE_TO_ENDPOINT[sourceType];
  if (!endpoint) {
    return null;
  }

  const includeVex = query.includeVex === "Y" ? "Y" : "N";
  const title = query.title ? String(query.title) : "";
  const params = { includeVex };

  if (sourceType === SBOM_READABLE_VIEW_SOURCE.VERIFICATION) {
    if (!query.verificationReportSeq) return null;
    params.verificationReportSeq = query.verificationReportSeq;
  } else if (sourceType === SBOM_READABLE_VIEW_SOURCE.MERGED) {
    if (!query.mergedSbomSeq) return null;
    params.mergedSbomSeq = query.mergedSbomSeq;
  } else if (sourceType === SBOM_READABLE_VIEW_SOURCE.PRODUCT) {
    if (!query.productSeq) return null;
    params.productSeq = query.productSeq;
  } else if (sourceType === SBOM_READABLE_VIEW_SOURCE.GROUP_PRODUCT) {
    if (!query.productSeq) return null;
    params.productSeq = query.productSeq;
    if (query.groupProductSeq) {
      params.groupProductSeq = query.groupProductSeq;
    }
  }

  return { endpoint, params, title, sourceType };
}

/**
 * Open the React readable-view route in a new tab (real app URL).
 * The new page fetches JSON and renders HTML.
 *
 * @param {object} options
 * @param {string} options.sourceType - one of SBOM_READABLE_VIEW_SOURCE.*
 * @param {Record<string, string|number|boolean|undefined>} options.params
 * @param {string} [options.title]
 */
export function openSbomReadableView({ sourceType, params = {}, title }) {
  const search = new URLSearchParams();
  search.set("sourceType", sourceType);

  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === "") return;
    search.set(key, String(value));
  });

  if (title) {
    search.set("title", title);
  }

  const url = `${basename}${paths.sbomReadableView}?${search.toString()}`;
  const previewWindow = window.open(url, "_blank");
  if (!previewWindow) {
    throwPopupBlocked();
  }
  return previewWindow;
}

/**
 * Open readable view; toast when the browser blocks the popup.
 * @param {object} options - same as openSbomReadableView
 * @param {string} [errorTitleKey="toast.export.error.title"] - i18n key for toast title
 * @returns {Window|null}
 */
export function openSbomReadableViewOrToast(
  options,
  errorTitleKey = "toast.export.error.title",
) {
  try {
    return openSbomReadableView(options);
  } catch (error) {
    if (error?.code === "POPUP_BLOCKED") {
      ToastConstant(
        TOAST.error,
        i18n.t(errorTitleKey),
        i18n.t("project.verification-report.popup-blocked"),
      );
      return null;
    }
    throw error;
  }
}
